/**
 * Pętla bez szwu z dowolnego pliku.
 *
 * **The loop is solved once here, not once per file.** Cutting a seamless loop
 * by hand is fiddly and a bad one is worse than a big file: a seam returning
 * every twenty seconds becomes the only thing anybody hears. Instead the same
 * buffer is scheduled over and over, each copy starting a crossfade before the
 * last one ends, so any recording loops cleanly — including one that plainly
 * does not loop on disk.
 *
 * **Equal power, not equal gain.** Two uncorrelated copies of an ambience
 * crossfaded on straight lines dip in loudness at the halfway point, and a dip
 * arriving on a schedule is exactly the artefact this is meant to avoid. The
 * curves are a quarter sine and its mirror, which keeps the sum of powers flat.
 *
 * **Several recordings take turns.** Given a list, the copies go A, B, A, B…
 * with the same crossfade between them as between two copies of one — which is
 * how two takes of a building site are woven into one sound that does not
 * repeat every ten seconds.
 *
 * **Scheduled ahead on a timer, not from `onended`.** Web Audio's ended event
 * fires after the fact, which is too late to have started the next copy. The
 * timer only decides *what* to schedule; the times themselves come off the
 * audio clock, so nothing drifts when the tab is busy.
 */

/** How far ahead copies are queued, and how often the queue is topped up. */
const LOOKAHEAD = 2.0;
const PUMP_MS = 500;

/** Points in the fade curves. Enough to be smooth, few enough to be free. */
const CURVE = 64;

const fadeIn = new Float32Array(CURVE);
const fadeOut = new Float32Array(CURVE);
for (let i = 0; i < CURVE; i += 1) {
  const t = i / (CURVE - 1);
  fadeIn[i] = Math.sin((t * Math.PI) / 2);
  fadeOut[i] = Math.cos((t * Math.PI) / 2);
}

export class CrossfadeLoop {
  private timer?: ReturnType<typeof setInterval>;
  /** When the next copy begins, on the audio clock. */
  private nextAt = 0;
  private live = new Set<AudioBufferSourceNode>();
  /** How long the two copies overlap. Trimmed for a buffer too short for it. */
  private readonly fade: number;
  private readonly buffers: AudioBuffer[];
  /** Which recording plays next. */
  private turn = 0;

  constructor(
    private readonly ctx: AudioContext,
    buffer: AudioBuffer | AudioBuffer[],
    private readonly out: AudioNode,
    fade = 2
  ) {
    this.buffers = Array.isArray(buffer) ? buffer : [buffer];
    // A crossfade needs the two curves not to overlap, so it can never take more
    // than a third of the shortest buffer from each end.
    const shortest = Math.min(...this.buffers.map((b) => b.duration));
    this.fade = Math.min(fade, shortest / 3);
  }

  get running(): boolean {
    return this.timer !== undefined;
  }

  start(): void {
    if (this.timer) return;
    // A beat ahead of now: scheduling into the past starts the copy immediately
    // and truncates its fade-in, which is audible as a click.
    this.nextAt = this.ctx.currentTime + 0.05;
    this.pump();
    this.timer = setInterval(() => this.pump(), PUMP_MS);
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = undefined;
    for (const source of this.live) {
      try {
        source.stop();
      } catch {
        // Already finished. Stopping a spent source throws and means nothing.
      }
    }
    this.live.clear();
  }

  private pump(): void {
    while (this.nextAt < this.ctx.currentTime + LOOKAHEAD) {
      const buffer = this.buffers[this.turn];
      this.turn = (this.turn + 1) % this.buffers.length;
      this.schedule(buffer, this.nextAt);
      this.nextAt += buffer.duration - this.fade;
    }
  }

  private schedule(buffer: AudioBuffer, at: number): void {
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;

    const gain = this.ctx.createGain();
    gain.gain.value = 0;
    gain.gain.setValueCurveAtTime(fadeIn, at, this.fade);
    gain.gain.setValueCurveAtTime(fadeOut, at + buffer.duration - this.fade, this.fade);

    source.connect(gain).connect(this.out);
    source.start(at);
    source.stop(at + buffer.duration);

    this.live.add(source);
    source.onended = () => {
      this.live.delete(source);
      gain.disconnect();
      source.disconnect();
    };
  }
}
